import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { PexIcon } from '../icons/PexIcon'
import { cn } from '../../lib/utils'
import { DialogPortal } from './dialog.lib'
import { useT } from '../../i18n'
import { sharedUiI18n } from '../../i18n/components/shared-ui'

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-slate-900/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  mobilePresentation?: 'centered' | 'fullscreen'
  hideCloseOnMobile?: boolean
}

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, mobilePresentation = 'centered', hideCloseOnMobile = false, onEscapeKeyDown, ...props }, ref) => {
  const t = useT(sharedUiI18n)
  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Centered modal (works on mobile + desktop). Mobile cap via
        // calc(100vw - 1.5rem) keeps 12px margin on each side. Consumer can
        // override max-w via className (e.g. max-w-[1200px] w-[97vw]).
        'fixed left-1/2 top-1/2 z-50 grid w-full max-w-[calc(100vw-1.5rem)] sm:max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border border-[var(--color-border)] bg-[var(--color-surface)] p-4 sm:p-6 text-[var(--color-text)] shadow-lg rounded-lg max-h-[calc(100dvh-1.5rem)] sm:max-h-[90vh] overflow-y-auto',
        mobilePresentation === 'fullscreen' &&
          'max-sm:inset-0 max-sm:h-dvh max-sm:w-dvw max-sm:max-h-none max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0 max-sm:p-0',
        className,
      )}
      {...props}
      onEscapeKeyDown={(event) => {
        onEscapeKeyDown?.(event)
        // Radix listens in document capture, before a nested picker can close
        // itself. Let an open picker consume the first Escape in its handler.
        if (event.target instanceof Element && event.target.closest('[data-dialog-escape-boundary]')) {
          event.preventDefault()
        }
      }}
    >
      {children}
      <DialogPrimitive.Close
        className={cn(
          'absolute right-3 top-3 sm:right-4 sm:top-4 rounded-md p-1.5 text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)] focus:outline-none focus-visible:shadow-[var(--shadow-focus)] min-h-11 min-w-11 sm:min-h-[28px] sm:min-w-[28px] flex items-center justify-center',
          hideCloseOnMobile && 'max-sm:hidden',
        )}
      >
        <PexIcon name="close" className="sm:h-4 sm:w-4" size="md" tone="inherit" />
        <span className="sr-only">{t.dialog.close}</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

export function DialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-col space-y-1.5 text-left', className)}
      {...props}
    />
  )
}

export function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  )
}

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold text-[var(--color-text)]', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-[var(--color-text-muted)]', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName
